import Dialog from "@mui/material/Dialog";
import styled from "styled-components";
import { Colors } from "../../../theme/v2/variants";
import ButtonComponent, {
  ButtonState,
  ButtonType,
} from "../../../components/Button/ButtonComponent";
import LoadingLottieJson from "../../../assets/loading.json";
import { useLottie } from "lottie-react";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { spacing } from "@mui/system";
import { TextField as MuiTextField } from "@mui/material";
import { ActionCardProps } from "../../../components/ActionCard/ActionCard";
import { useEffect, useState } from "react";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/pt-br";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { MOBILE_BREAKPOINT } from "../../../utils/mobileDimen";

interface DateFilterPopUpProps {
  openDialog: boolean;
  isLoadingDialog?: boolean;
  dates: DateFilterType | undefined;
  onCancelDialogCallback: () => void;
  onConfirmDialogCallback: (action: DateFilterType) => void;
  doNotChangeEndDateWhenChangeBeginDate?: boolean;
  cleanFilter?: () => void;
}

export interface DateFilterType {
  startDate: Date;
  deadlineDate: Date;
}

const DateFilter: React.FC<DateFilterPopUpProps> = ({
  openDialog,
  isLoadingDialog,
  dates,
  onCancelDialogCallback,
  onConfirmDialogCallback,
  doNotChangeEndDateWhenChangeBeginDate = false,
  cleanFilter,
}) => {
  const options = {
    animationData: LoadingLottieJson,
    loop: true,
    autoplay: true,
    style: { width: 120, height: 120 },
  };
  const { View } = useLottie(options);
  const [beginDate, setBeginDate] = useState<Date | undefined>(
    dates?.startDate
  );
  const [endDate, setEndDate] = useState<Date | undefined>(dates?.deadlineDate);

  useEffect(() => {
    setBeginDate(dates?.startDate);
    setEndDate(dates?.deadlineDate);
  }, [dates]);

  const beginDateOnChange = (date: any) => {
    setBeginDate(date.toDate());
    if (!doNotChangeEndDateWhenChangeBeginDate) {
      const datePlusServenDays = new Date(date.toDate());
      datePlusServenDays.setDate(datePlusServenDays.getDate() + 7);
      setEndDate(datePlusServenDays);
    }
  };

  const endDateOnChange = (date: any) => {
    setEndDate(date.toDate());
  };

  const onConfirmDialog = () => {
    if (dates && beginDate && endDate) {
      dates.startDate = beginDate;
      dates.deadlineDate = endDate;
      onConfirmDialogCallback(dates);
      return;
    }

    if (beginDate && endDate) {
      onConfirmDialogCallback({ startDate: beginDate, deadlineDate: endDate });
      return;
    }

    onConfirmDialogCallback({
      startDate: new Date(),
      deadlineDate: new Date(),
    });
  };

  const onCancelDialog = () => {
    onCancelDialogCallback();
  };

  return (
    <BootstrapDialog open={openDialog}>
      {!isLoadingDialog ? (
        <DialogContainer>
          <HeaderContainer>
            <DialogTitle>Filtrar por datas</DialogTitle>
            <CloseButton onClick={onCancelDialog}>X</CloseButton>
          </HeaderContainer>
          <DialogDescription></DialogDescription>

          <DatePickerContainer>
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale="pt-br"
            >
              <DesktopDatePicker
                label="Data início"
                value={beginDate}
                onChange={(date) => beginDateOnChange(date)}
                renderInput={(params) => (
                  <TextField variant="filled" size="small" {...params} />
                )}
                dayOfWeekFormatter={(weekday) => {
                  switch (weekday) {
                    case "Do":
                      return "D";
                    case "2ª":
                      return "S";
                    case "3ª":
                      return "T";
                    case "4ª":
                      return "Q";
                    case "5ª":
                      return "Q";
                    case "6ª":
                      return "S";
                    case "Sá":
                      return "S";
                    default:
                      return " ";
                  }
                }}
              />
            </LocalizationProvider>
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale="pt-br"
            >
              <DesktopDatePicker
                label="Data fim"
                value={endDate}
                onChange={(date) => endDateOnChange(date)}
                renderInput={(params) => (
                  <TextField variant="filled" size="small" {...params} />
                )}
                dayOfWeekFormatter={(weekday) => {
                  switch (weekday) {
                    case "Do":
                      return "D";
                    case "2ª":
                      return "S";
                    case "3ª":
                      return "T";
                    case "4ª":
                      return "Q";
                    case "5ª":
                      return "Q";
                    case "6ª":
                      return "S";
                    case "Sá":
                      return "S";
                    default:
                      return " ";
                  }
                }}
              />
            </LocalizationProvider>
          </DatePickerContainer>

          <ButtonContainer>
            <ButtonComponent
              text="Limpar filtros"
              type={ButtonType.TRANSPARENT}
              color={Colors.neutralGrayDark}
              fullWidth
              onClick={cleanFilter}
              startIcon={
                <DeleteOutlinedIcon style={{ color: Colors.balticSea }} />
              }
            />
            <ButtonComponent
              state={ButtonState.DEFAULT_ENABLED}
              fullWidth
              text="Confirmar"
              onClick={onConfirmDialog}
            />
          </ButtonContainer>
        </DialogContainer>
      ) : null}
      {isLoadingDialog === true ? (
        <LoadingDialogContainer>{View}</LoadingDialogContainer>
      ) : null}
    </BootstrapDialog>
  );
};

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  max-width: 100%;
  margin-bottom: 2px;
`;

const CloseButton = styled(CloseIcon)`
  cursor: pointer;
  font-size: 24px;
`;

const CleanFilterContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-top: 18px;
`;

const BootstrapDialog = styled(Dialog)`
  .MuiDialog-paper {
    padding: 0;
    border-radius: 16px;
    box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);

    @media (max-width: ${MOBILE_BREAKPOINT}px) {
      width: 95vw;
    }
  }
`;

const DialogContainer = styled.div`
  display: grid;
  /* grid-template-columns: 1fr;
  grid-template-rows: auto auto; */
  /* width: 397px; */
  height: max-content;
  background: white;
  grid-template-columns: 1fr;
  grid-template-rows: auto auto;
  padding-left: 24px;
  padding-right: 24px;
  padding-top: 24px;
  padding-bottom: 24px;
  align-content: start;
`;

const DialogTitle = styled.div`
  color: ${Colors.primaryColor};
  font-size: 24px;
  font-family: "Noto Sans";
  font-weight: 700;
  margin-top: 12px;

  word-wrap: break-word;
`;

const DialogDescription = styled.div`
  color: ${Colors.primaryColor};
  font-size: 16px;
  font-family: "Noto Sans";
  font-weight: 400;
  word-wrap: break-word;
  margin-top: 16px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 40px;
  gap: 24px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    gap: 16px;
  }
`;

const LoadingDialogContainer = styled.div`
  display: grid;
  width: 301px;
  height: 216px;
  background: white;
  justify-content: center;
  align-content: center;
`;

const TextField = styled(MuiTextField)<{ my?: number }>(spacing);

const DatePickerContainer = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 16px;
  margin-top: 20px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

export default DateFilter;
